import {
  Document,
  Page,
  View,
  Text,
  Image,
  StyleSheet,
  Font,
  Svg,
  Path,
  Line,
} from "@react-pdf/renderer";

const DEFAULT_PRIMARY = "#0C3E71";
const GRAY_100 = "#f3f4f6";
const GRAY_200 = "#e5e7eb";
const GRAY_300 = "#d1d5db";
const GRAY_400 = "#9ca3af";
const GRAY_500 = "#6b7280";
const GRAY_600 = "#4b5563";
const GRAY_700 = "#374151";
const GRAY_900 = "#111827";
const WHITE = "#ffffff";

const s = StyleSheet.create({
  page: { padding: 16, fontFamily: "Helvetica", fontSize: 9, color: GRAY_900 },
  // Header
  header: { backgroundColor: WHITE, borderRadius: 8, paddingHorizontal: 16, paddingVertical: 12, flexDirection: "row", justifyContent: "space-between", alignItems: "center" },
  logoRow: { flexDirection: "row", alignItems: "center", gap: 6 },
  logoText: { fontSize: 24, fontFamily: "Helvetica-Bold" },
  operatorBlock: { alignItems: "flex-end", gap: 2 },
  operatorName: { fontSize: 10, fontFamily: "Helvetica-Bold" },
  operatorDetail: { fontSize: 8, color: GRAY_600 },
  // Client bar
  clientBar: { backgroundColor: DEFAULT_PRIMARY, borderRadius: 8, paddingHorizontal: 12, height: 24, flexDirection: "row", alignItems: "center", gap: 8, marginTop: 16 },
  clientField: { color: WHITE, fontSize: 8 },
  // Main blue container
  blueBox: { backgroundColor: DEFAULT_PRIMARY, borderRadius: 8, padding: 12, marginTop: 16, gap: 12 },
  // Aircraft + Leg summary row
  summaryRow: { flexDirection: "row", gap: 12 },
  aircraftPanel: { backgroundColor: WHITE, borderRadius: 8, overflow: "hidden", flex: 1 },
  aircraftImage: { width: "100%", height: 110, objectFit: "cover" },
  aircraftImagePlaceholder: { width: "100%", height: 110, backgroundColor: GRAY_100 },
  aircraftInfo: { padding: 12 },
  aircraftTail: { fontSize: 14, fontFamily: "Helvetica-Bold" },
  aircraftMeta: { flexDirection: "row", gap: 8, marginTop: 4, fontSize: 7, color: GRAY_500 },
  legSummaryPanel: { backgroundColor: WHITE, borderRadius: 8, padding: 12, flex: 2 },
  legSummaryTitle: { fontSize: 11, fontFamily: "Helvetica-Bold", marginBottom: 6 },
  legSummaryRow: { flexDirection: "row", alignItems: "center", gap: 10, marginBottom: 6, fontSize: 7, color: GRAY_600 },
  legSummaryLegNum: { fontFamily: "Helvetica-Bold", color: GRAY_900, fontSize: 8 },
  legSummaryCode: { fontFamily: "Helvetica-Bold", fontSize: 7 },
  codePill: { flexDirection: "row", alignItems: "center", gap: 3, backgroundColor: GRAY_100, borderRadius: 4, paddingHorizontal: 5, paddingVertical: 2 },
  summaryField: { flexDirection: "row", alignItems: "center", gap: 3 },
  // Leg detail
  legLabel: { color: WHITE, fontSize: 9, fontFamily: "Helvetica-Bold", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 },
  sectionLabel: { color: "rgba(255,255,255,0.7)", fontSize: 7, fontFamily: "Helvetica-Bold", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 },
  dashedSep: { marginVertical: 12, height: 2, width: "100%" },
  legDetailRow: { flexDirection: "row", gap: 12 },
  legLeftCard: { backgroundColor: WHITE, borderRadius: 8, padding: 12, flex: 2 },
  legSideCard: { backgroundColor: WHITE, borderRadius: 8, padding: 12, flex: 1 },
  codeRow: { flexDirection: "row", alignItems: "center", gap: 6, marginTop: 6 },
  codeText: { fontSize: 22, fontFamily: "Helvetica-Bold", color: GRAY_900 },
  codeLine: { flex: 1, height: 0.5, backgroundColor: GRAY_300 },
  timeGrid: { flexDirection: "row", justifyContent: "space-between", marginTop: 10 },
  timeCol: { gap: 1 },
  timeLabel: { fontSize: 6, color: GRAY_400, textTransform: "uppercase" },
  timeValue: { fontSize: 7, color: GRAY_500 },
  timeValueBold: { fontSize: 9, fontFamily: "Helvetica-Bold", color: GRAY_900 },
  durationText: { fontSize: 7, color: GRAY_500, textAlign: "center" },
  // Airport fields
  airportLabel: { fontSize: 6, color: GRAY_400, textTransform: "uppercase", marginBottom: 2 },
  airportName: { fontSize: 9, fontFamily: "Helvetica-Bold", color: GRAY_900 },
  airportAddress: { fontSize: 7, color: GRAY_500, marginTop: 2 },
  // Crew & Passengers
  crewPassRow: { flexDirection: "row", alignItems: "stretch", gap: 12, marginTop: 12 },
  crewCol: { flex: 1, flexDirection: "column" },
  passCol: { flex: 1, flexDirection: "column" },
  whiteCard: { backgroundColor: WHITE, borderRadius: 8, padding: 12 },
  crewGrid: { flexDirection: "row", flexWrap: "wrap", gap: 6 },
  crewItem: { width: "48%", backgroundColor: GRAY_100, borderRadius: 4, paddingHorizontal: 6, paddingVertical: 4 },
  crewName: { fontSize: 8, fontFamily: "Helvetica-Bold", color: GRAY_900 },
  crewPhone: { fontSize: 7, color: GRAY_500 },
  passGrid: { flexDirection: "row", flexWrap: "wrap", gap: 6 },
  passItem: { width: "48%", backgroundColor: GRAY_100, borderRadius: 4, paddingHorizontal: 6, paddingVertical: 4 },
  passName: { fontSize: 8, color: GRAY_900 },
  // Services table
  serviceTable: { backgroundColor: WHITE, borderRadius: 8, overflow: "hidden" },
  serviceHeaderRow: { flexDirection: "row", borderBottomWidth: 0.5, borderBottomColor: GRAY_200, paddingVertical: 4, paddingHorizontal: 8 },
  serviceHeaderCell: { fontSize: 6, color: GRAY_500, textTransform: "uppercase", fontFamily: "Helvetica-Bold" },
  serviceRow: { flexDirection: "row", borderBottomWidth: 0.5, borderBottomColor: GRAY_100, paddingVertical: 6, paddingHorizontal: 8, alignItems: "center" },
  serviceCell: { fontSize: 8, color: GRAY_600 },
  serviceCellBold: { fontSize: 8, fontFamily: "Helvetica-Bold", color: GRAY_900 },
  // Footer
  footer: { textAlign: "center", fontSize: 7, color: "rgba(255,255,255,0.4)", marginTop: 4 },
  // placeholders
  placeholder: { color: GRAY_400 },
});

const val = (v) => v || "-";

// SVG Icon components using @react-pdf Svg/Path primitives
const Icon = ({ d, size = 10, color = GRAY_900, rotate }) => (
  <Svg viewBox="0 0 24 24" width={size} height={size} style={rotate ? { transform: `rotate(${rotate}deg)` } : undefined}>
    <Path d={d} fill={color} fillRule="evenodd" />
  </Svg>
);

const ICON_PATHS = {
  plane: "M14 8.94737L22 14V16L14 13.4737V18.8333L17 20.5V22L12.5 21L8 22V20.5L11 18.8333V13.4737L3 16V14L11 8.94737V3.5C11 2.67157 11.6716 2 12.5 2C13.3284 2 14 2.67157 14 3.5V8.94737Z",
  mapPin: "M12 20.8995L16.9497 15.9497C19.6834 13.2161 19.6834 8.78392 16.9497 6.05025C14.2161 3.31658 9.78392 3.31658 7.05025 6.05025C4.31658 8.78392 4.31658 13.2161 7.05025 15.9497L12 20.8995ZM12 23.7279L5.63604 17.364C2.12132 13.8492 2.12132 8.15076 5.63604 4.63604C9.15076 1.12132 14.8492 1.12132 18.364 4.63604C21.8787 8.15076 21.8787 13.8492 18.364 17.364L12 23.7279ZM12 13C13.1046 13 14 12.1046 14 11C14 9.89543 13.1046 9 12 9C10.8954 9 10 9.89543 10 11C10 12.1046 10.8954 13 12 13ZM12 15C9.79086 15 8 13.2091 8 11C8 8.79086 9.79086 7 12 7C14.2091 7 16 8.79086 16 11C16 13.2091 14.2091 15 12 15Z",
  phone: "M9.36556 10.6821C10.302 12.3288 11.6712 13.698 13.3179 14.6344L14.2024 13.3961C14.4965 12.9845 15.0516 12.8573 15.4956 13.0998C16.9024 13.8683 18.4571 14.3353 20.0789 14.4637C20.599 14.5049 21 14.9389 21 15.4606V19.9234C21 20.4361 20.6122 20.8657 20.1022 20.9181C19.5723 20.9726 19.0377 21 18.5 21C9.93959 21 3 14.0604 3 5.5C3 4.96227 3.02742 4.42771 3.08189 3.89776C3.1343 3.38775 3.56394 3 4.07665 3H8.53942C9.0611 3 9.49513 3.40104 9.5363 3.92109C9.66467 5.54288 10.1317 7.09764 10.9002 8.50444C11.1427 8.9484 11.0155 9.50354 10.6039 9.79757L9.36556 10.6821ZM6.84425 10.0252L8.7442 8.66809C8.20547 7.50514 7.83628 6.27183 7.64727 5H5.00907C5.00303 5.16632 5 5.333 5 5.5C5 12.9558 11.0442 19 18.5 19C18.667 19 18.8337 18.997 19 18.9909V16.3527C17.7282 16.1637 16.4949 15.7945 15.3319 15.2558L13.9748 17.1558C13.4258 16.9425 12.8956 16.6915 12.3874 16.4061L12.3293 16.373C10.3697 15.2587 8.74134 13.6303 7.627 11.6707L7.59394 11.6126C7.30849 11.1044 7.05754 10.5742 6.84425 10.0252Z",
  sun: "M12 18C8.68629 18 6 15.3137 6 12C6 8.68629 8.68629 6 12 6C15.3137 6 18 8.68629 18 12C18 15.3137 15.3137 18 12 18ZM12 16C14.2091 16 16 14.2091 16 12C16 9.79086 14.2091 8 12 8C9.79086 8 8 9.79086 8 12C8 14.2091 9.79086 16 12 16ZM11 1H13V4H11V1ZM11 20H13V23H11V20ZM3.51472 4.92893L4.92893 3.51472L7.05025 5.63604L5.63604 7.05025L3.51472 4.92893ZM16.9497 18.364L18.364 16.9497L20.4853 19.0711L19.0711 20.4853L16.9497 18.364ZM19.0711 3.51472L20.4853 4.92893L18.364 7.05025L16.9497 5.63604L19.0711 3.51472ZM5.63604 16.9497L7.05025 18.364L4.92893 20.4853L3.51472 19.0711L5.63604 16.9497ZM23 11V13H20V11H23ZM4 11V13H1V11H4Z",
  takeoff: "M21.949 10.1118C22.1634 10.912 21.6886 11.7345 20.8884 11.9489L5.2218 16.1467C4.77856 16.2655 4.31138 16.0674 4.08866 15.6662L1.46582 10.9415L2.91471 10.5533L5.3825 12.9979L10.4778 11.6326L5.96728 4.55896L7.89913 4.04132L14.8505 10.4609L20.1119 9.05113C20.9121 8.83671 21.7346 9.31159 21.949 10.1118ZM4.00013 19H20.0001V21H4.00013V19Z",
  landing: "M20.949 14.8882C20.7346 15.6884 19.9121 16.1633 19.1119 15.9489L3.44528 11.751C3.00205 11.6323 2.69653 11.2271 2.70424 10.7683L2.79514 5.36515L4.24403 5.75338L5.15891 9.10435L10.2542 10.4696L9.88479 2.08844L11.8166 2.60608L14.6269 11.6413L19.8883 13.0511C20.6885 13.2655 21.1634 14.088 20.949 14.8882ZM4.00008 19H20.0001V21H4.00008V19Z",
  arrowRight: "M16.1716 10.9999L10.8076 5.63589L12.2218 4.22168L20 11.9999L12.2218 19.778L10.8076 18.3638L16.1716 12.9999H4V10.9999H16.1716Z",
  time: "M12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22ZM12 20C16.4183 20 20 16.4183 20 12C20 7.58172 16.4183 4 12 4C7.58172 4 4 7.58172 4 12C4 16.4183 7.58172 20 12 20ZM13 12H17V14H11V7H13V12Z",
  calendar: "M17 3H21C21.5523 3 22 3.44772 22 4V20C22 20.5523 21.5523 21 21 21H3C2.44772 21 2 20.5523 2 20V4C2 3.44772 2.44772 3 3 3H7V1H9V3H15V1H17V3ZM20 11H4V19H20V11ZM15 5H9V7H7V5H4V9H20V5H17V7H15V5Z",
  user: "M4 22C4 17.5817 7.58172 14 12 14C16.4183 14 20 17.5817 20 22H18C18 18.6863 15.3137 16 12 16C8.68629 16 6 18.6863 6 22H4ZM12 13C8.685 13 6 10.315 6 7C6 3.685 8.685 1 12 1C15.315 1 18 3.685 18 7C18 10.315 15.315 13 12 13ZM12 11C14.21 11 16 9.21 16 7C16 4.79 14.21 3 12 3C9.79 3 8 4.79 8 7C8 9.21 9.79 11 12 11Z",
};

const AirportSection = ({ title, data }) => (
  <View>
    <Text style={s.airportLabel}>{title}</Text>
    <Text style={s.airportName}>{val(data.airportName)}</Text>
    <Text style={s.airportAddress}>{val(data.address)}</Text>
  </View>
);

const TripSheetPdf = ({ data, brandColor }) => {
  const PRIMARY = brandColor || DEFAULT_PRIMARY;

  // Build dynamic overrides for styles that use PRIMARY
  const dynamicStyles = {
    clientBar: { ...s.clientBar, backgroundColor: PRIMARY },
    blueBox: { ...s.blueBox, backgroundColor: PRIMARY },
  };

  return (
  <Document>
    <Page size="A4" style={s.page}>
      {/* HEADER */}
      <View style={s.header}>
        <View style={s.logoRow}>
          {data.operatorLogo ? (
             <View style={{ backgroundColor: PRIMARY, borderRadius: 6, padding: 6, alignItems: "center", justifyContent: "center" }}>
              <Image
                src={data.operatorLogo}
                style={{ width: 100, height: 60, objectFit: "contain" }}
              />
            </View>
          ) : (
            <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
              <Image src="/images/icons/planeright.svg" style={{ width: 20, height: 20 }} />
              <Text style={s.logoText}>Logo</Text>
            </View>
          )}
        </View>
        <View style={s.operatorBlock}>
          <Text style={s.operatorName}>{val(data.operator.name)}</Text>
          <Text style={s.operatorDetail}>{val(data.operator.address)}</Text>
          <Text style={s.operatorDetail}>{val(data.operator.phone)}</Text>
          <Text style={s.operatorDetail}>{val(data.operator.fax)}</Text>
        </View>
      </View>

      {/* CLIENT BAR */}
      <View style={dynamicStyles.clientBar}>
        <Text style={[s.clientField, { flex: 1, fontFamily: "Helvetica-Bold" }]}>{val(data.client.name)}</Text>
        <Text style={[s.clientField, { flex: 1 }]}>{val(data.client.company)}</Text>
        <Text style={[s.clientField, { flex: 1.3 }]}>{val(data.client.email)}</Text>
        <Text style={[s.clientField, { flex: 1, textAlign: "right" }]}>{val(data.client.phone)}</Text>
      </View>

      {/* MAIN BLUE CONTAINER */}
      <View style={dynamicStyles.blueBox}>
        {/* Aircraft + Leg Summary */}
        <View style={s.summaryRow}>
          <View style={s.aircraftPanel}>
            {data.aircraft.image ? (
              <Image
                src={
                  typeof data.aircraft.image === "string" && data.aircraft.image.startsWith("data:")
                    ? data.aircraft.image
                    : { uri: Array.isArray(data.aircraft.image) ? data.aircraft.image[0] : data.aircraft.image, method: "GET" }
                }
                style={s.aircraftImage}
              />
            ) : (
              <View style={s.aircraftImagePlaceholder} />
            )}
            <View style={s.aircraftInfo}>
              <Text style={s.aircraftTail}>{val(data.aircraft.tailNumber)}</Text>
              <View style={s.aircraftMeta}>
                <View style={{ flexDirection: "row", alignItems: "center", gap: 2 }}>
                  <Icon d={ICON_PATHS.takeoff} size={8} color={GRAY_500} />
                  <Text>{val(data.aircraft.name)}</Text>
                </View>
                <View style={{ flexDirection: "row", alignItems: "center", gap: 2 }}>
                  <Icon d={ICON_PATHS.user} size={7} color={GRAY_500} />
                  <Text>{val(String(data.aircraft.passengerCount))}</Text>
                </View>
              </View>
            </View>
          </View>

          <View style={s.legSummaryPanel}>
            <Text style={s.legSummaryTitle}>Leg Summary</Text>
            {data.legs.map((leg, idx) => (
              <View key={idx} style={s.legSummaryRow}>
                <Text style={s.legSummaryLegNum}>LEG {leg.legNumber}</Text>
                <View style={s.codePill}>
                  <Icon d={ICON_PATHS.takeoff} size={7} color={PRIMARY} />
                  <Text style={s.legSummaryCode}>{val(leg.departure.code)}</Text>
                </View>
                <View style={s.summaryField}>
                  <Icon d={ICON_PATHS.calendar} size={7} color={GRAY_400} />
                  <Text>{val(leg.departure.date)}</Text>
                </View>
                <View style={s.summaryField}>
                  <Icon d={ICON_PATHS.time} size={7} color={GRAY_400} />
                  <Text>{val(leg.departure.time)}</Text>
                </View>
                <Icon d={ICON_PATHS.arrowRight} size={8} color={GRAY_700} />
                <View style={s.codePill}>
                  <Icon d={ICON_PATHS.landing} size={7} color={PRIMARY} />
                  <Text style={s.legSummaryCode}>{val(leg.arrival.code)}</Text>
                </View>
                <View style={s.summaryField}>
                  <Icon d={ICON_PATHS.calendar} size={7} color={GRAY_400} />
                  <Text>{val(leg.arrival.date)}</Text>
                </View>
                <View style={s.summaryField}>
                  <Icon d={ICON_PATHS.time} size={7} color={GRAY_400} />
                  <Text>{val(leg.arrival.time)}</Text>
                </View>
              </View>
            ))}
          </View>
        </View>

        {/* LEG DETAILS */}
        {data.legs.map((leg, idx) => (
          <View key={idx} wrap={false}>
            {idx > 0 && (
              <Svg style={s.dashedSep} viewBox="0 0 1000 2" preserveAspectRatio="none">
                <Line
                  x1="0"
                  y1="1"
                  x2="1000"
                  y2="1"
                  stroke="#ffffff"
                  strokeOpacity="0.3"
                  strokeWidth="2"
                  strokeDasharray="8 6"
                />
              </Svg>
            )}
            <Text style={s.legLabel}>Leg {leg.legNumber}</Text>

            {/* Leg detail row: 3 separate cards */}
            <View style={s.legDetailRow}>
              {/* Left: Codes & Times */}
              <View style={s.legLeftCard}>
                <View style={{ flexDirection: "row", justifyContent: "space-between" }}>
                  <Text style={{ fontSize: 6, color: GRAY_400, textTransform: "uppercase" }}>{val(leg.timezone)}</Text>
                  <Text style={{ fontSize: 6, color: GRAY_400, textTransform: "uppercase" }}>{val(leg.flightDistance)}</Text>
                </View>
                <View style={s.codeRow}>
                  <Text style={s.codeText}>{val(leg.departure.code)}</Text>
                  <View style={{ flex: 1, flexDirection: "row", alignItems: "center", gap: 4 }}>
                    <View style={s.codeLine} />
                    <Icon d={ICON_PATHS.plane} size={14} color={PRIMARY} rotate={90} />
                    <View style={s.codeLine} />
                  </View>
                  <Text style={s.codeText}>{val(leg.arrival.code)}</Text>
                </View>
                <View style={s.timeGrid}>
                  <View style={s.timeCol}>
                    <Text style={s.timeValueBold}>{val(leg.departure.time)}</Text>
                    <Text style={s.timeValue}>{val(leg.departure.date)}</Text>
                    <Text style={s.timeValue}>{val(leg.departure.day)}</Text>
                  </View>
                  <View style={[s.timeCol, { alignItems: "center" }]}>
                    <Text style={s.durationText}>{val(leg.flightDuration)}</Text>
                  </View>
                  <View style={[s.timeCol, { alignItems: "flex-end" }]}>
                    <Text style={s.timeValueBold}>{val(leg.arrival.time)}</Text>
                    <Text style={s.timeValue}>{val(leg.arrival.date)}</Text>
                    <Text style={s.timeValue}>{val(leg.arrival.day)}</Text>
                  </View>
                </View>
              </View>

              {/* Center: Departure Airport */}
              <View style={s.legSideCard}>
                <AirportSection title="Departure" data={leg.departure} />
              </View>

              {/* Right: Arrival Airport */}
              <View style={s.legSideCard}>
                <AirportSection title="Arrival" data={leg.arrival} />
              </View>
            </View>

            {/* Crew & Passengers - only show if either has data */}
            {(leg.crew.length > 0 || leg.passengers.length > 0) && (
              <View style={s.crewPassRow}>
                {leg.crew.length > 0 && (
                  <View style={leg.passengers.length > 0 ? s.crewCol : { flex: 1, flexDirection: "column" }}>
                    <Text style={s.sectionLabel}>Crew</Text>
                    <View style={[s.whiteCard, { flexGrow: 1 }]}>
                      <View style={s.crewGrid}>
                        {leg.crew.map((member, i) => (
                          <View key={i} style={s.crewItem}>
                            <Text style={s.crewName}>{val(member.name)}</Text>
                            <Text style={s.crewPhone}>{val(member.phone)}</Text>
                          </View>
                        ))}
                      </View>
                    </View>
                  </View>
                )}
                {leg.passengers.length > 0 && (
                  <View style={leg.crew.length > 0 ? s.passCol : { flex: 1, flexDirection: "column" }}>
                    <Text style={s.sectionLabel}>Passengers</Text>
                    <View style={[s.whiteCard, { flexGrow: 1 }]}>
                      <View style={s.passGrid}>
                        {leg.passengers.map((passenger, i) => (
                          <View key={i} style={s.passItem}>
                            <Text style={s.passName}>{val(typeof passenger === "object" ? passenger.fullName || "" : passenger)}</Text>
                          </View>
                        ))}
                      </View>
                    </View>
                  </View>
                )}
              </View>
            )}

            {/* Additional Services - only show if has data */}
            {leg.additionalServices.length > 0 && (
              <View style={{ marginTop: 12 }}>
                <Text style={s.sectionLabel}>Additional Services</Text>
                <View style={s.serviceTable}>
                  <View style={s.serviceHeaderRow}>
                    <Text style={[s.serviceHeaderCell, { flex: 1.2 }]}>Name</Text>
                    <Text style={[s.serviceHeaderCell, { flex: 1.5 }]}>Service Details</Text>
                    <Text style={[s.serviceHeaderCell, { flex: 1.2 }]}>Address</Text>
                    <Text style={[s.serviceHeaderCell, { flex: 1 }]}>Phone Number</Text>
                    <Text style={[s.serviceHeaderCell, { flex: 1.2 }]}>Confirmation Number</Text>
                  </View>
                  {leg.additionalServices.map((service, i) => (
                    <View key={i} style={s.serviceRow}>
                      <View style={{ flex: 1.2, flexDirection: "row", alignItems: "center", gap: 3 }}>
                        <Icon d={ICON_PATHS.plane} size={8} color={GRAY_900} />
                        <Text style={s.serviceCellBold}>{val(service.name)}</Text>
                      </View>
                      <Text style={[s.serviceCell, { flex: 1.5 }]}>{val(service.serviceDetails)}</Text>
                      <Text style={[s.serviceCell, { flex: 1.2 }]}>{val(service.address)}</Text>
                      <Text style={[s.serviceCell, { flex: 1 }]}>{val(service.phone)}</Text>
                      <Text style={[s.serviceCell, { flex: 1.2 }]}>{val(service.confirmationNumber)}</Text>
                    </View>
                  ))}
                </View>
              </View>
            )}
          </View>
        ))}
      </View>

      {/* FOOTER */}
      <Text style={s.footer}>Page 1 of 1</Text>
    </Page>
  </Document>
  );
};

export default TripSheetPdf;